import React, { useState } from 'react';
import { Progress, Button as RSButton, ButtonProps as RSButtonProps } from 'reactstrap';
import { FileDownloadResponse } from '~types/downloads';

export interface ButtonProps extends RSButtonProps {
  caption: string;
  classNames: string;
  size: string;
  loading?: boolean;
  handleDownload: (updateProgress?: (value: string) => void) => Promise<FileDownloadResponse | null>;
}

const DownloadButton: React.FC<ButtonProps> = (props) => {
  const { caption = 'Download', handleDownload, classNames = '', loading, children, ...restProps } = props;

  const [progress, setProgress] = useState({ value: 0, text: '' });
  const [downloading, setDownloading] = useState(false);

  const updateProgressValue = (value: any) => {
    const valueAsString = String(value);
    if (value.endsWith('%')) {
      const valueAsFloat = parseFloat(value.slice(0, -1));
      if (!isNaN(valueAsFloat)) {
        setProgress({ value: valueAsFloat, text: valueAsString });
      }
    } else {
      setProgress({ value: 100, text: '100%' });
    }
  };

  const extractFilename = (contentDisposition: string) => {
    let filename = 'file.zip';
    if (contentDisposition) {
      filename = contentDisposition.split('=')[1];
    }
    return filename;
  };

  const handleChange = async () => {
    setProgress({ value: 0, text: '0%' });
    setDownloading(true);
    try {
      const response = await handleDownload(updateProgressValue);

      if (response) {
        const filename = extractFilename(response.headers['content-disposition']);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        setProgress({ value: 0, text: '0%' });
        link.click();
        link.remove();
      } else {
        setDownloading(false);
      }
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setProgress({ value: 0, text: '0%' });
      setDownloading(false);
    }
  };

  return (
    <>
      {downloading && <Progress className={classNames} animated value={progress.value}>{`${progress.text}`}</Progress>}
      {!downloading && (
        <RSButton className={classNames} disabled={loading || restProps.disabled} {...restProps} onClick={handleChange}>
          {children}
          {caption}
        </RSButton>
      )}
    </>
  );
};

export default DownloadButton;
